/**
 * features/common/ReceiptCapture.tsx
 * Capture a receipt for an expense: on mobile the "Scan" button opens
 * the camera directly; "Upload" accepts an existing image or PDF.
 * Images are downscaled client-side before upload to keep Storage
 * light; PDFs upload as-is. Returns the download URL via onChange.
 */
import { useRef, useState } from 'react';
import { Camera, Upload, X, FileText, Loader2, ExternalLink } from 'lucide-react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      res(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      rej(new Error('Could not read the image.'));
    };
    img.src = url;
  });
}

async function compressImage(file: File, maxDim = 1600, quality = 0.82): Promise<Blob> {
  const img = await loadImage(file);
  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > maxDim) {
    const s = maxDim / longest;
    width = Math.round(width * s);
    height = Math.round(height * s);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, width, height);
  return new Promise((res) => canvas.toBlob((b) => res(b ?? file), 'image/jpeg', quality));
}

export function ReceiptCapture({
  value,
  onChange,
  folder,
  scanLabel = 'Scan receipt',
  uploadLabel = 'Upload image / PDF',
  viewLabel = 'View receipt',
}: {
  value?: string;
  onChange: (url: string | null, name?: string) => void;
  folder: string;
  scanLabel?: string;
  uploadLabel?: string;
  viewLabel?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const camRef = useRef<HTMLInputElement>(null);
  const upRef = useRef<HTMLInputElement>(null);

  const isPdf = !!value && /\.pdf($|\?)/i.test(value);

  async function handle(file?: File) {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      let blob: Blob = file;
      let ext: string;
      let contentType: string;
      if (file.type.startsWith('image/')) {
        blob = await compressImage(file);
        ext = 'jpg';
        contentType = 'image/jpeg';
      } else if (file.type === 'application/pdf') {
        ext = 'pdf';
        contentType = 'application/pdf';
      } else {
        throw new Error('Please choose an image or a PDF.');
      }
      const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, blob, { contentType });
      onChange(await getDownloadURL(r), file.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(false);
      if (camRef.current) camRef.current.value = '';
      if (upRef.current) upRef.current.value = '';
    }
  }

  return (
    <div>
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
      />
      <input
        ref={upRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
      />

      {value ? (
        <div className="flex items-center gap-3 rounded-xl border border-black/10 p-2.5 dark:border-white/10">
          {isPdf ? (
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-brandSoft text-brand">
              <FileText size={22} />
            </div>
          ) : (
            <img src={value} alt="Receipt" className="h-14 w-14 rounded-lg object-cover" />
          )}
          <a href={value} target="_blank" rel="noreferrer" className="flex flex-1 items-center gap-1 text-sm font-medium text-brand hover:underline">
            {viewLabel} <ExternalLink size={13} />
          </a>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-black/5 hover:text-red-600 dark:hover:bg-white/5"
            aria-label="Remove receipt"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => camRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200 px-3 py-2 text-sm font-semibold text-primary transition hover:border-brand hover:bg-brandSoft disabled:opacity-60 dark:border-white/15 dark:text-gray-200"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} {scanLabel}
          </button>
          <button
            type="button"
            onClick={() => upRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200 px-3 py-2 text-sm font-semibold text-primary transition hover:border-brand hover:bg-brandSoft disabled:opacity-60 dark:border-white/15 dark:text-gray-200"
          >
            <Upload size={16} /> {uploadLabel}
          </button>
        </div>
      )}
      {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
